import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Eye, Plus, Heart, Brain, Users, SmilePlus, Frown, Meh, Calendar } from "lucide-react";
import BehaviorAnalysisPanel from "@/components/observations/BehaviorAnalysisPanel";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "health", label: "صحي", icon: Heart, color: "text-red-500" },
  { value: "psychological", label: "نفسي", icon: Brain, color: "text-purple-500" },
  { value: "behavioral", label: "سلوكي", icon: Users, color: "text-blue-500" },
] as const;

const POSITIVE_TAGS = ["متعاون", "مركز", "نشيط", "مبادر", "اجتماعي", "مبدع", "هادئ", "منظم"];
const NEGATIVE_TAGS = ["عصبي", "منسحب", "مشتت", "عدواني", "قلق", "خامل", "بكاء", "عنيد"];

const SENTIMENT_MAP = {
  positive: { label: "إيجابي", icon: SmilePlus, color: "text-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800" },
  neutral: { label: "محايد", icon: Meh, color: "text-amber-500 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800" },
  negative: { label: "سلبي", icon: Frown, color: "text-red-500 bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800" },
};

const ChildObservations = () => {
  const { user, profile } = useAuth();
  const nurseryId = profile?.nursery_id;
  const queryClient = useQueryClient();

  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedChildId, setSelectedChildId] = useState("");
  const [category, setCategory] = useState<string>("behavioral");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [showForm, setShowForm] = useState(false);

  // Fetch classes
  const { data: classes } = useQuery({
    queryKey: ["obs-classes", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("classes").select("id, name").eq("nursery_id", nurseryId!).order("name");
      return data || [];
    },
    enabled: !!nurseryId,
  });

  // Fetch children for selected class
  const { data: children } = useQuery({
    queryKey: ["obs-children", selectedClassId],
    queryFn: async () => {
      const { data } = await supabase.from("children").select("id, full_name, photo_url").eq("class_id", selectedClassId).order("full_name");
      return data || [];
    },
    enabled: !!selectedClassId,
  });

  // Fetch observations for selected child
  const { data: observations, isLoading: obsLoading } = useQuery({
    queryKey: ["observations", selectedChildId],
    queryFn: async () => {
      const { data } = await supabase
        .from("child_observations" as any)
        .select("*")
        .eq("child_id", selectedChildId)
        .order("observation_date", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(50);
      return data || [];
    },
    enabled: !!selectedChildId,
  });

  // Add observation mutation
  const addObservation = useMutation({
    mutationFn: async () => {
      if (!nurseryId || !user || !selectedChildId || selectedTags.length === 0) throw new Error("بيانات ناقصة");
      
      // Determine sentiment from tags
      const posCount = selectedTags.filter(t => POSITIVE_TAGS.includes(t)).length;
      const negCount = selectedTags.filter(t => NEGATIVE_TAGS.includes(t)).length;
      const sentiment = posCount > negCount ? "positive" : negCount > posCount ? "negative" : "neutral";

      const { error } = await supabase.from("child_observations" as any).insert({
        nursery_id: nurseryId,
        child_id: selectedChildId,
        observer_id: user.id,
        category,
        tags: selectedTags,
        sentiment,
        notes: notes.trim() || null,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم تسجيل الملاحظة بنجاح");
      setSelectedTags([]);
      setNotes("");
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ["observations", selectedChildId] });
    },
    onError: () => toast.error("فشل تسجيل الملاحظة"),
  });

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const selectedChildName = children?.find(c => c.id === selectedChildId)?.full_name;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">ملاحظات الأطفال اليومية</h1>
          <p className="text-muted-foreground text-sm mt-1">تسجيل ملاحظات صحية ونفسية وسلوكية عن كل طفل</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedClassId} onValueChange={(v) => { setSelectedClassId(v); setSelectedChildId(""); }}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="اختر الفصل..." />
            </SelectTrigger>
            <SelectContent>
              {(classes || []).map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedChildId} onValueChange={setSelectedChildId} disabled={!selectedClassId}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="اختر الطفل..." />
            </SelectTrigger>
            <SelectContent>
              {(children || []).map(c => (
                <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
              ))}
            </SelectContent>
        </Select>
          {selectedChildId && nurseryId && (
            <BehaviorAnalysisPanel
              childId={selectedChildId}
              childName={children?.find(c => c.id === selectedChildId)?.full_name || ""}
              nurseryId={nurseryId}
            />
          )}
        </div>
      </div>

      {!selectedChildId ? (
        <div className="text-center py-20">
          <Eye className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">اختر الفصل ثم الطفل لتسجيل أو عرض الملاحظات</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Add Observation Form */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="shadow-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Plus className="w-5 h-5 text-primary" />
                  تسجيل ملاحظة لـ {selectedChildName}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Category Selection */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">التصنيف</label>
                  <div className="flex gap-2">
                    {CATEGORIES.map(cat => (
                      <Button
                        key={cat.value}
                        variant={category === cat.value ? "default" : "outline"}
                        size="sm"
                        className="gap-1.5 flex-1"
                        onClick={() => setCategory(cat.value)}
                      >
                        <cat.icon className={`w-4 h-4 ${category === cat.value ? "" : cat.color}`} />
                        {cat.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Positive Tags */}
                <div>
                  <label className="text-sm font-medium text-emerald-600 mb-2 block flex items-center gap-1.5">
                    <SmilePlus className="w-4 h-4" /> سلوكيات إيجابية
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {POSITIVE_TAGS.map(tag => (
                      <Badge
                        key={tag}
                        variant={selectedTags.includes(tag) ? "default" : "outline"}
                        className={`cursor-pointer transition-all text-xs ${
                          selectedTags.includes(tag) 
                            ? "bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-500" 
                            : "hover:bg-emerald-50 dark:hover:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300"
                        }`}
                        onClick={() => toggleTag(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Negative Tags */}
                <div>
                  <label className="text-sm font-medium text-red-600 mb-2 block flex items-center gap-1.5">
                    <Frown className="w-4 h-4" /> سلوكيات سلبية
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {NEGATIVE_TAGS.map(tag => (
                      <Badge
                        key={tag}
                        variant={selectedTags.includes(tag) ? "default" : "outline"}
                        className={`cursor-pointer transition-all text-xs ${
                          selectedTags.includes(tag)
                            ? "bg-red-500 hover:bg-red-600 text-white border-red-500"
                            : "hover:bg-red-50 dark:hover:bg-red-950/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
                        }`}
                        onClick={() => toggleTag(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">ملاحظات إضافية (اختياري)</label>
                  <Textarea
                    placeholder="اكتب ملاحظاتك هنا..."
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                </div>

                <Button
                  className="w-full gap-2"
                  onClick={() => addObservation.mutate()}
                  disabled={selectedTags.length === 0 || addObservation.isPending}
                >
                  {addObservation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  تسجيل الملاحظة
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right: Observation History */}
          <div className="lg:col-span-2">
            <Card className="shadow-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  سجل الملاحظات
                </CardTitle>
              </CardHeader>
              <CardContent>
                {obsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : !observations || observations.length === 0 ? (
                  <div className="text-center py-12">
                    <Eye className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-muted-foreground text-sm">لا توجد ملاحظات مسجلة لهذا الطفل</p>
                  </div>
                ) : (
                  <ScrollArea className="max-h-[600px]">
                    <div className="space-y-3">
                      <AnimatePresence>
                        {observations.map((obs: any, i: number) => {
                          const catInfo = CATEGORIES.find(c => c.value === obs.category);
                          const sentInfo = SENTIMENT_MAP[obs.sentiment as keyof typeof SENTIMENT_MAP] || SENTIMENT_MAP.neutral;
                          const SentIcon = sentInfo.icon;
                          const CatIcon = catInfo?.icon || Brain;

                          return (
                            <motion.div
                              key={obs.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.03 }}
                              className={`p-4 rounded-lg border ${sentInfo.color}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <CatIcon className={`w-4 h-4 ${catInfo?.color}`} />
                                  <span className="text-sm font-medium">{catInfo?.label}</span>
                                  <SentIcon className="w-4 h-4" />
                                  <span className="text-xs">{sentInfo.label}</span>
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(obs.observation_date), "d MMM yyyy", { locale: ar })}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1.5 mb-2">
                                {(obs.tags || []).map((tag: string) => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                              {obs.notes && (
                                <p className="text-sm text-muted-foreground">{obs.notes}</p>
                              )}
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildObservations;
